import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { Box, Divider, Typography } from '@mui/material';

import { Icon } from '@verantos-dev/shared-components';
import { AnalyticalVariable } from '@verantos-dev/types-cohorts';

import { HEADER_ICON_SX } from '../analyticalVariableCard.constants';
import { AnalyticalVariableCardProvider } from './AnalyticalVariableCardContext';
import { useAnalyticalVariableCard } from './useAnalyticalVariableCard';
import type { ConceptGroupOption } from '../../types';
import {
  deriveCategoryFromAnalyticalVariable,
  hasConceptGroups,
} from '../../utils/analyticalVariable.utils';
import { AVFormatCard } from '../AVFormatCard/AVFormatCard';
import { AVLogicCard } from '../AVLogicCard/AVLogicCard';
import { AVSummaryCard } from '../AVSummaryCard/AVSummaryCard';

interface AnalyticalVariableCardProps {
  analyticalVariable: AnalyticalVariable;
  conceptGroups: ConceptGroupOption[];
  owner?: string;
  lastModified?: string;
  isEditable?: boolean;
  onAnalyticalVariableChange?: (updates: Partial<AnalyticalVariable>) => void;
  onDrillDownClick?: (analyticalVariableId: string) => void;
}

export const AnalyticalVariableCard = ({
  analyticalVariable,
  conceptGroups,
  owner,
  lastModified,
  isEditable,
  onAnalyticalVariableChange,
  onDrillDownClick,
}: AnalyticalVariableCardProps) => {
  const { showHeader, headerItems } = useAnalyticalVariableCard({
    analyticalVariable,
    owner,
    lastModified,
  });

  const category = deriveCategoryFromAnalyticalVariable(analyticalVariable);
  // OR-6069 follow-up: SQL-only AVs carry logic: null, so there's no
  // structured tree to render — skip the logic card entirely rather than
  // mounting an empty AVLogicCard body.
  const shouldShowLogic =
    analyticalVariable.logic === null
      ? false
      : category === 'observability'
      ? false
      : category === 'demographic'
      ? hasConceptGroups(analyticalVariable.logic)
      : true;

  return (
    <AnalyticalVariableCardProvider
      analyticalVariable={analyticalVariable}
      conceptGroups={conceptGroups}
      isEditable={isEditable}
      onAnalyticalVariableChange={onAnalyticalVariableChange}
      onDrillDownClick={onDrillDownClick}
    >
      {showHeader && (
        <Box
          data-testid="av-card-header"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          {headerItems.map((item) => (
            <Box
              key={item.icon}
              data-testid={`av-card-header-${item.icon}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {item.icon === 'category' && <CategoryOutlinedIcon sx={HEADER_ICON_SX} />}
              {item.icon === 'owner' && (
                <Icon
                  id="av-card-header-owner-icon"
                  icon="owner"
                  width={16}
                  height={16}
                  sx={{ fill: (theme) => theme.palette.text.secondary }}
                />
              )}
              {item.icon === 'lastModified' && <AccessTimeOutlinedIcon sx={HEADER_ICON_SX} />}
              <Typography variant="body2" color="text.secondary">
                {item.label} {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <AVSummaryCard />
      <AVFormatCard />
      {shouldShowLogic && (
        <>
          <Divider sx={{ my: 2 }} />
          <AVLogicCard />
        </>
      )}
    </AnalyticalVariableCardProvider>
  );
};
